import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, ShieldCheck, Info } from "lucide-react";
import { api } from "@/lib/api";
import { PAYMENTS } from "@/constants/testIds";

const CADENCES = [
    { value: "per_shift", label: "Per shift", note: "Payout triggered when each shift is completed (manual scheduler)." },
    { value: "daily", label: "Daily", note: "Native Stripe daily payouts." },
    { value: "weekly", label: "Weekly", note: "Native Stripe weekly payouts." },
    { value: "biweekly", label: "Biweekly", note: "App-driven manual payout every 14 days." },
];

const METHODS = [
    { value: "ach", label: "ACH bank transfer" },
    { value: "debit", label: "Debit card (instant)" },
    { value: "check", label: "Paper check (offline)" },
];

export default function PaymentSettings() {
    const [settings, setSettings] = useState({ cadence: "weekly", auto_pay: false, method: "ach" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/stripe/payout-settings");
                setSettings(data);
            } catch {
                // ignore
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    async function save() {
        setSaving(true);
        try {
            const { data } = await api.put("/stripe/payout-settings", settings);
            toast.success("Payment settings saved", {
                description: data.warning || undefined,
                id: PAYMENTS.saved,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    }

    if (loading) return (
        <div className="min-h-screen bg-stone-50"><TopNav /><div className="max-w-3xl mx-auto p-10 text-stone-500 font-dmsans">Loading...</div></div>
    );

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={PAYMENTS.panel} className="max-w-4xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Payment settings</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Payout cadence & method
                    </h1>
                    <p className="text-sm text-stone-500 mt-2 font-dmsans">
                        Control how and when caregivers get paid. Changes push to Stripe when cadence is natively supported.
                    </p>
                </div>

                <Card className="rounded-2xl border-stone-200 mb-6">
                    <CardContent className="p-8 space-y-8">
                        {/* Cadence */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Wallet className="w-4 h-4 text-emerald-700" />
                                <Label className="font-outfit text-base font-medium">Payout cadence</Label>
                            </div>
                            <Select value={settings.cadence} onValueChange={(v) => setSettings({ ...settings, cadence: v })}>
                                <SelectTrigger data-testid={PAYMENTS.cadenceSelect} className="h-12 rounded-xl">
                                    <SelectValue placeholder="Select cadence" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CADENCES.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="mt-2 text-xs text-stone-500 font-dmsans flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {CADENCES.find((c) => c.value === settings.cadence)?.note}
                            </p>
                        </div>

                        {/* Auto-pay */}
                        <div className="flex items-start justify-between p-5 rounded-xl border border-stone-200 bg-stone-50">
                            <div>
                                <div className="font-outfit text-base font-medium text-stone-900">Auto-pay</div>
                                <p className="text-sm text-stone-500 font-dmsans mt-1 max-w-md">
                                    Automatically release payouts on completion (per shift) or at the end of each period. Turn off to review before every batch.
                                </p>
                            </div>
                            <Switch
                                data-testid={PAYMENTS.autoPaySwitch}
                                checked={settings.auto_pay}
                                onCheckedChange={(v) => setSettings({ ...settings, auto_pay: v })}
                                className="data-[state=checked]:bg-emerald-600"
                            />
                        </div>

                        {/* Method */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                                <Label className="font-outfit text-base font-medium">Payout method</Label>
                            </div>
                            <Select value={settings.method} onValueChange={(v) => setSettings({ ...settings, method: v })}>
                                <SelectTrigger data-testid={PAYMENTS.methodSelect} className="h-12 rounded-xl">
                                    <SelectValue placeholder="Select method" />
                                </SelectTrigger>
                                <SelectContent>
                                    {METHODS.map((m) => (
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {settings.method === "check" && (
                                <p className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 font-dmsans">
                                    Check payouts are handled offline (Stripe does not process paper checks).
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end pt-2">
                            <Button
                                data-testid={PAYMENTS.saveBtn}
                                onClick={save}
                                disabled={saving}
                                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 h-11"
                            >
                                {saving ? "Saving..." : "Save settings"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
